import React, { useEffect, useState } from "react";
import { Badge, Toast, ToastContainer } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstTabs from "../../components/DstTabs/DstTabs";
import MultiSelectDropdown from "../../components/MultiSelectDropdown/MultiSelectDropdown";
import { useSession } from "../../contexts/SessionContext";
import { PermissionComponent } from "../../services/PermissionGuard";
import { isPast } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { CursusStage } from "./Course.d";
// import Meetings from "./components/Meetings/Meetings";
// import PedagogicalPath from "./components/PedagogicalPath/PedagogicalPath";
import { STAGE_STATUS_CONFIG, duplicateCursusStage, fetchCursusStages } from "./Course.function";
import AboutCourse from "./components/AboutCourse/AboutCourse";
import { CourseStageFlagsPopup } from "./components/CourseStageFlagsPopup/CourseStageFlagsPopup";
import CreateCursusStagePopup from "./components/CreateCursusStagePopup/CreateCursusStagePopup";
import EditCursusStagePopup from "./components/EditCursusStagePopup/EditCursusStagePopup";

const Course = () => {
  const { language } = useSession();
  const navigate = useNavigate();
  const [activeKey, setActiveKey] = useState<string>("about_course");
  const { course_id } = useParams();
  const [cursusStages, setCursusStages] = useState<CursusStage[]>([]);
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [cursusStagesFetchFailed, setCursusStagesFetchFailed] = useState(false);
  const [openCreatePopup, setOpenCreatePopup] = useState(false);
  const [openFlagsPopup, setOpenFlagsPopup] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [showDuplicateToast, setShowDuplicateToast] = useState(false);

  const selectedCursusStage = cursusStages.find((stage) => stage.id === selectedStageId);

  useEffect(() => {
    if (cursusStages.length > 0 && !cursusStages.find((stage) => stage.id === selectedStageId)) {
      setSelectedStageId(cursusStages[0].id);
    }
  }, [cursusStages, selectedStageId]);

  useEffect(() => {
    fetchCursusStages(course_id!, setCursusStages, setLoading, language, setCursusStagesFetchFailed);
  }, [course_id, language]);

  const _refetchCursusStages = () =>
    fetchCursusStages(course_id!, setCursusStages, setLoading, language, setCursusStagesFetchFailed);

  const _handleStageCreated = (stageId: number) => {
    fetchCursusStages(course_id!, setCursusStages, setLoading, language, setCursusStagesFetchFailed, () => {
      setSelectedStageId(stageId);
      setActiveKey("about_course");
    });
  };

  const _handleDuplicateStage = () => {
    if (!selectedCursusStage || isDuplicating) return;

    setShowDuplicateToast(false);
    duplicateCursusStage(selectedCursusStage.id, language, setIsDuplicating, (stageId: number) => {
      setShowDuplicateToast(true);
      _handleStageCreated(stageId);
    });
  };

  const creationPopup = openCreatePopup && (
    <CreateCursusStagePopup
      cursusId={course_id!}
      language={language}
      openPopup={openCreatePopup}
      setOpenPopup={setOpenCreatePopup}
      onStageCreated={_handleStageCreated}
    />
  );

  if (loading) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary mb-3" role="status" />
        <div>{translate(language, "PAGES.COURSE.LOADING")}</div>
      </div>
    );
  }

  if (cursusStagesFetchFailed) {
    return (
      <div className="text-center mt-3">
        <h2>{translate(language, "PAGES.COURSE.ERRORS.NOT_FOUND")}</h2>
        <DstButton
          value={translate(language, "PAGES.COURSE.BUTTONS.ALL_COURSES")}
          clickFunction={(event: any) => completeNavigate(event, navigate, `/courses`)}
          btnClass="mt-3"
        />
      </div>
    );
  }

  if (cursusStages.length === 0 || !selectedCursusStage) {
    return (
      <div className="text-center mt-3">
        <h2>{translate(language, "PAGES.COURSE.ERRORS.NO_STAGE")}</h2>

        <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/cursus/*/stages" }]}>
          <DstButton
            value={translate(language, "PAGES.COURSE.BUTTONS.CREATE_STAGE")}
            btnClass="ms-2 mt-3"
            btnImageBefore={<span className="me-2 dst-icon-plus" />}
            variant="primary"
            clickFunction={() => setOpenCreatePopup(true)}
          />
        </PermissionComponent>
        {creationPopup}
      </div>
    );
  }

  const _statusConfig =
    STAGE_STATUS_CONFIG[
      selectedCursusStage.is_disabled || isPast(selectedCursusStage.dt_end)
        ? "inactive"
        : isPast(selectedCursusStage.dt_start)
        ? "active"
        : "upcoming"
    ];

  const stageOptions = cursusStages.map((stage) => ({
    id: stage.id,
    name: `${stage.deployment_name || ""} - ${stage.cursus.name || ""} (#${stage.id})`,
  }));

  const tabsData = [
    {
      id: "about_course",
      title: (
        <React.Fragment>
          <span className="dst-icon-info-circle me-2" />
          {translate(language, "PAGES.COURSE.TABS.ABOUT_COURSE")}
        </React.Fragment>
      ),
      content: <AboutCourse course={selectedCursusStage} language={language} />,
    },
    // {
    //   id: "pedagogical_path",
    //   title: (
    //     <React.Fragment>
    //       <span className="dst-icon-book-open-01 me-2" />
    //       {translate(language, "PAGES.COURSE.TABS.PEDAGOGICAL_PATH")}
    //     </React.Fragment>
    //   ),
    //   content: <PedagogicalPath course={selectedCursusStage} language={language} />,
    // },
    // {
    //   id: "meetings",
    //   title: (
    //     <React.Fragment>
    //       <span className="dst-icon-video-recorder me-2" />
    //       {translate(language, "PAGES.COURSE.TABS.MEETINGS")}
    //     </React.Fragment>
    //   ),
    //   content: <Meetings />,
    // },
  ];

  return (
    <div className="m-3">
      <div className="d-flex flex-wrap gap-2 align-items-lg-center justify-content-between mb-3">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <h1 className="me-2">{selectedCursusStage.cursus.name}</h1>
          <Badge bg={_statusConfig.bg} className="me-2 text-white">
            <span className={`${_statusConfig.icon} me-1`} />
            {translate(language, _statusConfig.labelKey)}
          </Badge>
          <MultiSelectDropdown
            items={stageOptions}
            language={language}
            label={
              stageOptions.find((option) => option.id === selectedStageId)
                ? `${translate(language, "PAGES.COURSE.SELECT_STAGE")}: ${
                    stageOptions.find((option) => option.id === selectedStageId)?.name
                  }`
                : translate(language, "PAGES.COURSE.SELECT_STAGE")
            }
            isSingleSelect
            selectedItemLabel={translate(language, "COMPONENTS.MULTI_SELECT.SELECTED")}
            badgeItemProperty="name"
            displayItem={(item) => item.name}
            defaultSelectedItems={[stageOptions.find((option) => option.id === selectedStageId)]}
            onConfirm={(selected) => setSelectedStageId(selected[0]?.id ?? null)}
            disabled={cursusStages.length < 2}
            borderToggle
          />
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/cursus/stages/*/duplicate" }]}>
            <DstButton
              value={translate(language, "PAGES.COURSE.BUTTONS.DUPLICATE")}
              btnImageBefore={<span className="me-2 dst-icon-copy-03" />}
              variant="light"
              clickFunction={_handleDuplicateStage}
              disabled={isDuplicating}
              loading={isDuplicating}
            />
          </PermissionComponent>
          <PermissionComponent neededPermission={[{ methods: ["PUT"], route: "/cursus/stages/*/flags" }]}>
            <DstButton
              value={translate(language, "PAGES.COURSE.BUTTONS.EDIT_FLAGS")}
              btnImageBefore={<span className="me-2 dst-icon-edit-02" />}
              variant="primary"
              clickFunction={() => setOpenFlagsPopup(true)}
            />
          </PermissionComponent>
          <PermissionComponent neededPermission={[{ methods: ["PATCH"], route: "/cursus/stages/*" }]}>
            <DstButton
              value={translate(language, "PAGES.COURSE.BUTTONS.EDIT_STAGE")}
              btnImageBefore={<span className="me-2 dst-icon-edit-02" />}
              variant="primary"
              clickFunction={() => setOpenEditPopup(true)}
            />
          </PermissionComponent>
        </div>
      </div>

      <DstTabs
        activeKey={activeKey}
        variant="primary"
        onSelect={(key) => {
          if (key) {
            setActiveKey(key);
          }
        }}
        tabsData={tabsData}
      />

      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg="success"
          show={showDuplicateToast}
          onClose={() => setShowDuplicateToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{translate(language, "PAGES.COURSE.SUCCESS.DUPLICATE_STAGE")}</Toast.Body>
        </Toast>
      </ToastContainer>

      {creationPopup}

      <EditCursusStagePopup
        course={selectedCursusStage}
        language={language}
        openPopup={openEditPopup}
        setOpenPopup={setOpenEditPopup}
        onCourseUpdated={_refetchCursusStages}
      />

      <CourseStageFlagsPopup
        language={language}
        openPopup={openFlagsPopup}
        setOpenPopup={setOpenFlagsPopup}
        cursusStage={selectedCursusStage}
        onFlagsUpdated={(flags) => {
          setCursusStages((previousStages) =>
            previousStages.map((stage) => (stage.id === selectedCursusStage.id ? { ...stage, flags } : stage))
          );
        }}
      />
    </div>
  );
};

export default Course;
